import { useEffect, useMemo, useState } from "react";
import { Briefcase, Download, FileArchive, FileCode2, FileText, Loader2, MessageSquare, Receipt, ShoppingBag, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, beDate, eur, inputCls, btnPrimary } from "@/lib/studioApi";
import { EmptyState, ListSkeleton, StatusBadge } from "@/components/studio/StudioUI";
import { AccountingExport } from "@/components/studio/AccountingExport";
import { mediaUrl } from "@/lib/photos";
import { AccountantExports } from "@/components/studio/AccountantExports";
import { AccountantQuestions, AskQuestionDialog } from "@/components/studio/AccountantQuestions";

const TABS = [
  { key: "ventes", label: "Factures de vente", icon: Receipt },
  { key: "avoirs", label: "Notes de crédit", icon: Undo2 },
  { key: "achats", label: "Achats", icon: ShoppingBag },
  { key: "questions", label: "Mes questions", icon: MessageSquare },
];
const PURCHASE_STATUS = { a_payer: "À payer", payee: "Payée", a_verifier: "À vérifier" };

const yearOf = (d) => String(d || "").slice(0, 4);

export default function StudioAccountant() {
  const [tab, setTab] = useState("ventes");
  const [invoices, setInvoices] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [busy, setBusy] = useState("");
  const [exporting, setExporting] = useState(false);
  const [asking, setAsking] = useState(null);
  const [questionsKey, setQuestionsKey] = useState(0);

  useEffect(() => {
    studio.get("/invoices").then(({ data }) => setInvoices(data)).catch((e) => { setInvoices([]); toast.error(apiError(e, "Chargement impossible.")); });
    studio.get("/invoices/summary").then(({ data }) => setSummary(data)).catch(() => {});
    studio.get("/purchases").then(({ data }) => setPurchases(data.items || [])).catch(() => setPurchases([]));
  }, []);

  const years = useMemo(() => {
    const set = new Set([String(new Date().getFullYear())]);
    (invoices || []).forEach((i) => set.add(yearOf(i.issue_date)));
    (purchases || []).forEach((p) => set.add(yearOf(p.issue_date)));
    return [...set].filter(Boolean).sort().reverse();
  }, [invoices, purchases]);

  const sales = (invoices || []).filter((i) => yearOf(i.issue_date) === year && (tab === "avoirs" ? i.kind === "avoir" : i.kind !== "avoir") && i.status !== "brouillon");
  const buys = (purchases || []).filter((p) => yearOf(p.issue_date) === year);

  const download = async (inv, kind) => {
    setBusy(`${inv.id}-${kind}`);
    try {
      const res = await studio.get(`/invoices/${inv.id}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `${inv.reference}${kind === "ubl" ? "-ubl.xml" : ".pdf"}` });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e, "Téléchargement impossible.")); } finally { setBusy(""); }
  };

  const cards = summary ? [
    ["Facturé " + summary.year, eur(summary.invoiced_net), "HTVA, avoirs déduits"],
    ["TVA due", eur(summary.vat_due), "sur l'année"],
    ["En attente", eur(summary.open_amount), `${summary.open_count} facture(s)`],
    ["En retard", eur(summary.late_amount), `${summary.late_count} facture(s)`],
  ] : [];

  return (
    <StudioLayout
      title="Espace comptable"
      subtitle="Factures de vente (PDF + UBL Peppol), notes de crédit, achats et export par période. Consultation uniquement."
      action={
        <button type="button" data-testid="accountant-export-btn" onClick={() => setExporting(true)} className={btnPrimary}>
          <FileArchive className="h-4 w-4" /> Export comptable (ZIP)
        </button>
      }
    >
      {exporting && <AccountingExport onClose={() => setExporting(false)} />}
      {asking && <AskQuestionDialog invoice={asking} onClose={() => setAsking(null)} onAsked={() => setQuestionsKey((k) => k + 1)} />}

      {cards.length > 0 && (
        <div data-testid="accountant-summary" className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {cards.map(([label, value, hint]) => (
            <div key={label} className="rounded-2xl border border-[#E2E7F7] bg-white p-4">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">{label}</p>
              <p className="mt-1 font-heading text-lg font-bold text-[#0F2645]">{value}</p>
              <p className="text-xs text-[#5C6A87]">{hint}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div data-testid="accountant-tabs" className="flex flex-wrap gap-1 rounded-xl border border-[#E2E7F7] bg-white p-1">
          {TABS.map((t) => (
            <button key={t.key} type="button" data-testid={`accountant-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`inline-flex min-h-[40px] items-center gap-2 rounded-lg px-3.5 text-sm font-semibold transition-colors ${tab === t.key ? "bg-[#0F2645] text-white" : "text-[#5C6A87] hover:bg-[#F6F8FD]"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
        <select data-testid="accountant-year" className={`${inputCls} !w-auto min-w-[110px]`} value={year} onChange={(e) => setYear(e.target.value)}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {tab === "questions" ? (
        <AccountantQuestions key={questionsKey} />
      ) : (
      <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E2E7F7] bg-white">
        {tab !== "achats" ? (
          invoices === null ? <div className="p-4"><ListSkeleton /></div> : !sales.length ? (
            <EmptyState testId="accountant-sales-empty" icon={FileText} title={tab === "avoirs" ? "Aucune note de crédit sur cette année" : "Aucune facture de vente sur cette année"} hint="Changez d'année ou utilisez l'export pour une autre période." />
          ) : (
            <table data-testid="accountant-sales-table" className="w-full text-sm">
              <thead className="bg-[#F6F8FD] text-left text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">
                <tr><th className="px-4 py-3">Référence</th><th className="hidden px-4 py-3 sm:table-cell">Date</th><th className="hidden px-4 py-3 sm:table-cell">Client</th><th className="hidden px-4 py-3 md:table-cell">Statut</th><th className="px-4 py-3 text-right">TVAC</th><th className="px-4 py-3 text-right">Fichiers</th></tr>
              </thead>
              <tbody className="divide-y divide-[#EDEFF7]">
                {sales.map((inv) => (
                  <tr key={inv.id} data-testid={`accountant-invoice-${inv.id}`} className="hover:bg-[#FAFBFE]">
                    <td className="px-4 py-3 font-semibold text-[#0F2645]"><span className="font-mono text-xs">{inv.reference}</span><span className="block text-[11px] font-normal text-[#93A0BC]">{inv.kind_label}<span className="sm:hidden"> · {beDate(inv.issue_date)}</span></span></td>
                    <td className="hidden px-4 py-3 text-[#5C6A87] sm:table-cell">{beDate(inv.issue_date)}</td>
                    <td className="hidden px-4 py-3 text-[#0F2645] sm:table-cell">{inv.client_name}</td>
                    <td className="hidden px-4 py-3 md:table-cell"><StatusBadge value={inv.status} /></td>
                    <td className="px-4 py-3 text-right font-semibold text-[#0F2645]">{eur(inv.total)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button type="button" data-testid={`accountant-pdf-${inv.id}`} disabled={!!busy} onClick={() => download(inv, "pdf")} title="PDF" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E7F7] text-[#3A5CC8] hover:bg-[#EEF1FC]">
                          {busy === `${inv.id}-pdf` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        </button>
                        <button type="button" data-testid={`accountant-ubl-${inv.id}`} disabled={!!busy} onClick={() => download(inv, "ubl")} title="UBL Peppol" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E7F7] text-[#3A5CC8] hover:bg-[#EEF1FC]">
                          {busy === `${inv.id}-ubl` ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCode2 className="h-4 w-4" />}
                        </button>
                        <button type="button" data-testid={`accountant-ask-${inv.id}`} onClick={() => setAsking(inv)} title="Poser une question sur cette facture" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E7F7] text-[#0F2645] hover:bg-[#F6F8FD]">
                          <MessageSquare className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : purchases === null ? <div className="p-4"><ListSkeleton /></div> : !buys.length ? (
          <EmptyState testId="accountant-purchases-empty" icon={ShoppingBag} title="Aucun document d'achat sur cette année" hint="Les factures d'achat encodées ou reçues par Peppol apparaissent ici." />
        ) : (
          <table data-testid="accountant-purchases-table" className="w-full text-sm">
            <thead className="bg-[#F6F8FD] text-left text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">
              <tr><th className="px-4 py-3">Fournisseur</th><th className="hidden px-4 py-3 sm:table-cell">Date</th><th className="hidden px-4 py-3 md:table-cell">Statut</th><th className="px-4 py-3 text-right">TVAC</th><th className="px-4 py-3 text-right">Pièce</th></tr>
            </thead>
            <tbody className="divide-y divide-[#EDEFF7]">
              {buys.map((p) => (
                <tr key={p.id} data-testid={`accountant-purchase-${p.id}`} className="hover:bg-[#FAFBFE]">
                  <td className="px-4 py-3 font-semibold text-[#0F2645]">{p.supplier_name}<span className="block font-mono text-[11px] font-normal text-[#93A0BC]">{p.kind === "avoir" ? "Avoir" : "Facture"}{p.reference ? ` · ${p.reference}` : ""}<span className="sm:hidden"> · {beDate(p.issue_date)}</span></span></td>
                  <td className="hidden px-4 py-3 text-[#5C6A87] sm:table-cell">{beDate(p.issue_date)}</td>
                  <td className="hidden px-4 py-3 text-xs font-semibold text-[#5C6A87] md:table-cell">{PURCHASE_STATUS[p.status] || p.status}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#0F2645]">{eur(p.total)}</td>
                  <td className="px-4 py-3 text-right">
                    {p.media_id ? (
                      <a href={mediaUrl(p.media_id)} target="_blank" rel="noreferrer" data-testid={`accountant-purchase-doc-${p.id}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#E2E7F7] text-[#3A5CC8] hover:bg-[#EEF1FC]" title="Document">
                        <Download className="h-4 w-4" />
                      </a>
                    ) : <span className="text-xs text-[#93A0BC]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      <AccountantExports />

      <p className="mt-6 inline-flex items-start gap-2 text-xs leading-relaxed text-[#5C6A87]">
        <Briefcase className="h-3.5 w-3.5 shrink-0 text-[#3A5CC8]" />
        Accès en consultation : vous ne pouvez ni créer ni modifier de document. Les CSV de l'export utilisent le séparateur « ; » (Excel, WinBooks, Exact, Yuki, Billit, Odoo).
      </p>
    </StudioLayout>
  );
}
